import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { darken, lighten } from "@mui/material/styles";
import { FONTS, GLASS_SX } from "../constants/styles";
import TextLogo from "../img/TextLogo.svg";

interface OnboardingShellProps {
    variant: "form" | "celebration" | "landing";
    step?: number;
    totalSteps?: number;
    heroTitle?: string;
    heroSubtitle?: string;
    heroContent?: React.ReactNode;
    showLogo?: boolean;
    children: React.ReactNode;
    bottomButton?: React.ReactNode;
}

const NOISE_OVERLAY = {
    content: '""',
    position: "absolute" as const,
    inset: 0,
    opacity: 0.04,
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
    backgroundRepeat: "repeat",
    backgroundSize: "128px 128px",
    pointerEvents: "none" as const,
};

const StepPill: React.FC<{ step: number; total: number }> = ({
    step,
    total,
}) => (
    <Box
        sx={{
            ...GLASS_SX,
            borderRadius: "20px",
            px: 2,
            py: 0.5,
            display: "inline-flex",
            alignSelf: "center",
        }}
    >
        <Typography
            sx={{
                fontFamily: FONTS.display,
                fontSize: "13px",
                fontWeight: 600,
                color: "white",
            }}
        >
            Step {step} of {total}
        </Typography>
    </Box>
);

const OnboardingShell: React.FC<OnboardingShellProps> = ({
    variant,
    step,
    totalSteps = 3,
    heroTitle,
    heroSubtitle,
    heroContent,
    showLogo = true,
    children,
    bottomButton,
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    const gradient = `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`;

    if (variant === "celebration") {
        return (
            <Stack
                width="100%"
                sx={{
                    minHeight: "100dvh",
                    background: gradient,
                    transition: "background 0.4s ease",
                    position: "relative",
                    "&::before": {
                        content: '""',
                        position: "absolute" as const,
                        inset: 0,
                        background: `radial-gradient(ellipse 80% 50% at 50% 30%, rgba(255,255,255,0.10) 0%, transparent 70%)`,
                        pointerEvents: "none" as const,
                    },
                    "&::after": NOISE_OVERLAY,
                }}
            >
                <Stack
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        flex: 1,
                        alignItems: "center",
                    }}
                >
                    {showLogo && (
                        <Box
                            sx={{
                                pt: "calc(env(safe-area-inset-top) + 20px)",
                                pb: 1,
                            }}
                        >
                            <img
                                src={TextLogo}
                                alt="Jemloc"
                                style={{
                                    width: 120,
                                    height: 28,
                                    filter: "brightness(0) invert(1)",
                                }}
                            />
                        </Box>
                    )}
                    {children}
                </Stack>
            </Stack>
        );
    }

    const isLanding = variant === "landing";
    const heroMinHeight = isLanding ? "30vh" : heroContent ? "240px" : "200px";

    return (
        <Stack
            width="100%"
            sx={{
                minHeight: "100dvh",
                bgcolor: "white",
                overflowX: "hidden",
            }}
        >
            {/* Hero */}
            <Box
                sx={{
                    background: gradient,
                    transition: "background 0.4s ease",
                    position: "relative",
                    minHeight: heroMinHeight,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    pt: "calc(env(safe-area-inset-top) + 20px)",
                    pb: heroContent ? "56px" : "40px",
                    px: 2,
                    boxSizing: "border-box",
                    "&::after": NOISE_OVERLAY,
                }}
            >
                <Stack
                    spacing={1}
                    alignItems="center"
                    sx={{ position: "relative", zIndex: 1, width: "100%" }}
                >
                    {showLogo && (
                        <Box sx={{ mb: 1 }}>
                            <img
                                src={TextLogo}
                                alt="Jemloc"
                                style={{
                                    width: 120,
                                    height: 28,
                                    filter: "brightness(0) invert(1)",
                                }}
                            />
                        </Box>
                    )}
                    {step != null && (
                        <StepPill step={step} total={totalSteps} />
                    )}
                    {heroTitle && (
                        <Typography
                            sx={{
                                fontFamily: FONTS.display,
                                fontWeight: 700,
                                fontSize: isLanding ? "28px" : "22px",
                                color: "white",
                                textAlign: "center",
                            }}
                        >
                            {heroTitle}
                        </Typography>
                    )}
                    {heroSubtitle && (
                        <Typography
                            sx={{
                                fontFamily: FONTS.body,
                                fontWeight: 400,
                                fontSize: "14px",
                                color: "rgba(255,255,255,0.8)",
                                textAlign: "center",
                            }}
                        >
                            {heroSubtitle}
                        </Typography>
                    )}
                    {heroContent}
                </Stack>
            </Box>

            {/* White content sheet */}
            <Stack
                sx={{
                    bgcolor: "white",
                    borderTopLeftRadius: "24px",
                    borderTopRightRadius: "24px",
                    mt: "-24px",
                    position: "relative",
                    zIndex: 1,
                    px: 2,
                    pt: 3,
                    pb: bottomButton
                        ? "calc(env(safe-area-inset-bottom) + 100px)"
                        : "calc(env(safe-area-inset-bottom) + 32px)",
                    boxSizing: "border-box",
                    flex: 1,
                    "@keyframes fadeSlideIn": {
                        from: { opacity: 0, transform: "translateY(12px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: "fadeSlideIn 0.3s ease-out",
                }}
                spacing={2}
            >
                {children}
            </Stack>

            {bottomButton}
        </Stack>
    );
};

export default OnboardingShell;
